import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, Trash2, Heart, PackageOpen, ArrowRight, ArrowLeft } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { api, fmt, fmtPerUnit, fmtQty, getSessionPin, getSessionQuote } from "@/lib/api";
import AbandonedCartRecovery from "@/components/AbandonedCartRecovery";

export default function CartPage() {
  const { cart, cartLoading, setQty, removeItem, saveForLater, gstEnabled } = useStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [creditAccount, setCreditAccount] = useState(null);
  const sessionQuote = getSessionQuote();
  const sessionPin = getSessionPin();

  useEffect(() => {
    if (!user) { setCreditAccount(null); return; }
    api.get("/credit/account").then(({ data }) => setCreditAccount(data)).catch(() => setCreditAccount(null));
  }, [user]);

  return (
    <div className="min-h-screen pb-24 pt-28" data-testid="cart-page">
      <div className="mx-auto max-w-6xl px-6 lg:px-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="mb-3 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-amber-500">
            <span className="h-px w-10 bg-amber-500" />
            Your Selection
          </p>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-1 sm:text-4xl">Your Cart</h1>
          <p className="mt-2 text-sm text-3" data-testid="cart-page-count">
            {cart.count} {cart.count === 1 ? "item" : "items"} ready for checkout
          </p>
        </motion.div>

        <AbandonedCartRecovery />

        {cartLoading ? (
          <div className="mt-12 space-y-4">
            {[1, 2].map((i) => (
              <div key={i} className="h-28 animate-pulse rounded-2xl border border-line bg-surface" />
            ))}
          </div>
        ) : cart.items.length === 0 ? (
          <div className="mt-12 flex flex-col items-center gap-4 rounded-2xl border border-line bg-surface py-24 text-center shadow-card" data-testid="cart-empty-state">
            <span className="flex h-20 w-20 items-center justify-center rounded-2xl border border-line bg-hover text-amber-500">
              <PackageOpen className="h-9 w-9" strokeWidth={1.4} />
            </span>
            <h2 className="font-display text-2xl font-semibold tracking-tight text-1 sm:text-3xl">Your cart is empty</h2>
            <p className="max-w-sm text-sm text-3">
              Find the packaging and shipping essentials you need.
            </p>
            <button
              data-testid="cart-continue-shopping"
              onClick={() => navigate("/shop")}
              className="mt-3 rounded-full bg-amber-500 px-8 py-4 text-sm font-bold text-black transition-all hover:bg-amber-400 hover:shadow-[0_0_32px_rgba(255,159,28,0.35)]"
            >
              Continue Shopping
            </button>
          </div>
        ) : (
          <div className="mt-10 grid gap-8 lg:grid-cols-3">
            <div className="space-y-4 lg:col-span-2">
              {cart.issues?.length > 0 && (
                <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.06] px-5 py-3.5 text-sm text-amber-300" data-testid="cart-issues">
                  {cart.issues.map((i) => i.issue).join(" · ")}
                </div>
              )}
              {cart.items.map((item) => (
                <motion.div
                  key={`${item.product_id}-${item.sku}`}
                  layout
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex gap-5 rounded-2xl border border-line bg-surface p-5 shadow-card transition-shadow duration-300 hover:shadow-lift"
                  data-testid={`cart-page-item-${item.sku}`}
                >
                  <div
                    className="h-24 w-24 shrink-0 cursor-pointer overflow-hidden rounded-xl border border-line bg-white p-2 sm:h-28 sm:w-28"
                    onClick={() => navigate(`/shop/product/${item.product_id}`)}
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      loading="lazy"
                      className="h-full w-full object-contain"
                    />
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p
                          className="cursor-pointer truncate font-display text-base font-medium text-1 hover:text-amber-400"
                          onClick={() => navigate(`/shop/product/${item.product_id}`)}
                        >
                          {item.name}
                        </p>
                        <p className="mt-0.5 text-xs text-3">{item.variant_label} · SKU {item.sku}</p>
                        <p className="mt-0.5 text-[11px] text-3" data-testid={`cart-page-unitprice-${item.sku}`}>
                          {fmtPerUnit(item.unit_price, item.unit)} × {fmtQty(item.qty, item.unit)}
                          {gstEnabled && ` · GST ${item.gst_rate ?? 18}%`}
                        </p>
                      </div>
                      <span className="shrink-0 font-display text-lg font-semibold text-amber-500">
                        {fmt(Math.round(item.unit_price * item.qty * 100) / 100)}
                      </span>
                    </div>
                    <div className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-3">
                      <div className="flex items-center rounded-xl border border-line">
                        <button data-testid={`cart-page-minus-${item.sku}`} onClick={() => {
                          const inc = item.qty_increment || 1;
                          const moq = item.min_order_qty || 1;
                          const next = item.qty - inc;
                          if (next < moq) removeItem(item.product_id, item.sku);
                          else setQty(item.product_id, item.sku, next);
                        }} className="flex h-9 w-9 items-center justify-center text-2 hoverable-t1" aria-label="Decrease">
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="min-w-9 px-1 text-center text-sm font-bold" data-testid={`cart-page-qty-${item.sku}`}>{item.qty.toLocaleString("en-IN")}</span>
                        <button data-testid={`cart-page-plus-${item.sku}`} onClick={() => setQty(item.product_id, item.sku, item.qty + (item.qty_increment || 1))} className="flex h-9 w-9 items-center justify-center text-2 hoverable-t1" aria-label="Increase">
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <div className="flex gap-4">
                        <button data-testid={`cart-page-save-${item.sku}`} onClick={() => saveForLater(item)} className="flex items-center gap-1.5 text-xs font-medium text-3 transition-colors hover:text-emerald-400">
                          <Heart className="h-3.5 w-3.5" /> Save for later
                        </button>
                        <button data-testid={`cart-page-remove-${item.sku}`} onClick={() => removeItem(item.product_id, item.sku)} className="flex items-center gap-1.5 text-xs font-medium text-3 transition-colors hover:text-red-400">
                          <Trash2 className="h-3.5 w-3.5" /> Remove
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
              <button
                data-testid="cart-back-to-shop"
                onClick={() => navigate("/shop")}
                className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-3 transition-colors hover:text-amber-400"
              >
                <ArrowLeft className="h-4 w-4" /> Continue Shopping
              </button>
            </div>

            <div className="h-fit rounded-2xl border border-line bg-surface p-6 shadow-card lg:sticky lg:top-24" data-testid="cart-summary">
              <h3 className="font-display text-lg font-medium tracking-tight text-1">Order Summary</h3>
              <div className="mt-5 space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-2">Subtotal</span>
                  <span className="font-bold text-1" data-testid="cart-subtotal">{fmt(cart.subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-2">Shipping</span>
                  <span className="text-3">At checkout</span>
                </div>
                {gstEnabled && (
                  <div className="flex justify-between">
                    <span className="text-2">GST</span>
                    <span className="text-3">At checkout</span>
                  </div>
                )}
                <div className="border-t border-line pt-3">
                  <div className="flex justify-between">
                    <span className="font-bold text-1">Estimated Total</span>
                    <span className="font-display text-xl font-semibold text-amber-500">{fmt(cart.subtotal)}</span>
                  </div>
                </div>
              </div>
              <p className="mt-3 text-[11px] leading-relaxed text-3">
                Free delivery on orders ₹1,000+ within eligible PAQVERO delivery zones. Coupons can be applied at checkout.
              </p>
              {sessionQuote?.serviceable && cart.subtotal > 0 && (() => {
                const threshold = sessionQuote.threshold || 1000;
                const eligible = sessionQuote.free_delivery_eligible;
                const free = eligible && cart.subtotal >= threshold;
                const away = Math.max(0, Math.round((threshold - cart.subtotal) * 100) / 100);
                const progress = eligible ? Math.min(100, Math.round((cart.subtotal / threshold) * 100)) : 0;
                return (
                  <div className="mt-3 rounded-xl border border-line bg-hover px-4 py-3 text-[11px] leading-relaxed" data-testid="cart-delivery-note">
                    <span className="text-2">Delivery to <span className="font-mono font-bold text-1">{sessionPin}</span> ({sessionQuote.zone_label}): </span>
                    {free ? (
                      <span className="font-bold text-emerald-500" data-testid="cart-delivery-free">FREE — you've unlocked free delivery!</span>
                    ) : (
                      <span className="font-bold text-1" data-testid="cart-delivery-charge">{fmt(sessionQuote.standard.charge)}</span>
                    )}
                    {eligible && (
                      <div className="mt-2" data-testid="cart-delivery-progress">
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${free ? "bg-emerald-500" : "bg-amber-500"}`}
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        {!free && (
                          <span className="mt-1.5 block font-semibold text-amber-500" data-testid="cart-delivery-unlock">Add {fmt(away)} more to unlock FREE delivery.</span>
                        )}
                      </div>
                    )}
                    <span className="mt-1 block text-3">Estimated delivery: {sessionQuote.standard.estimate?.text}</span>
                  </div>
                );
              })()}
              {user && creditAccount?.credit_status === "APPROVED" && (
                <div className="mt-3 rounded-xl border border-line bg-hover px-4 py-3 text-[11px] leading-relaxed" data-testid="cart-credit-note">
                  <span className="text-2">Available PAQVERO Credit: </span>
                  <span className="font-bold text-1">{fmt(creditAccount.available_credit)}</span>
                  {creditAccount.available_credit >= cart.subtotal ? (
                    <span className="block text-emerald-400">PAQVERO Credit available for this order</span>
                  ) : (
                    <span className="block text-amber-400">{fmt(cart.subtotal - creditAccount.available_credit)} exceeds your available PAQVERO Credit — you can still pay now.</span>
                  )}
                </div>
              )}
              <button
                data-testid="cart-checkout-btn"
                onClick={() => navigate("/checkout")}
                className="group mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 py-4 text-sm font-bold text-black transition-all hover:bg-amber-400 hover:shadow-[0_0_32px_rgba(255,159,28,0.35)]"
              >
                Proceed to Checkout
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
